<template>
  <div
    ref="root"
    class="rd rd--warehouse"
    :data-w="layout"
    :data-density="density"
    :data-bar="barSticky ? 'sticky' : 'flow'"
    :style="rootStyle"
  >
    <div class="rd-main">
      <report-nav :groups="menuGroups" :current="currentKey" :label="$t('routes.warehouse')" @select="openScreen">
        <template #tools>
          <button
            type="button"
            class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain"
            :title="`${$t('report_desktop.shortcuts')} (?)`"
            :aria-label="$t('report_desktop.shortcuts')"
            :aria-expanded="String(helpOpen)"
            @click="helpOpen = !helpOpen"
          >
            <v-icon size="18">mdi-keyboard-outline</v-icon>
          </button>
          <button
            type="button"
            class="ui-text-btn rd-nav__legacy"
            :title="$t('report_desktop.old_view')"
            @click="openLegacy"
          >
            <v-icon size="16" color="#c2410c">mdi-history</v-icon>
            <span class="rd-nav__legacy-label">{{ $t("report_desktop.old_view") }}</span>
          </button>
        </template>
      </report-nav>

      <report-filter-bar
        ref="bar"
        :from="from"
        :to="to"
        :presets="presets"
        :quick-keys="screen.quick || defaultQuick"
        :show-period="Boolean(screen.filters.period)"
        :period-locked="screen.filters.period === 'day'"
        :show-shifts="Boolean(screen.filters.shifts)"
        :shifts="filters.selectedShifts"
        :search="search"
        :loading="loading"
        @period="setPeriod"
        @update:shifts="setShifts"
        @update:search="search = $event"
        @refresh="reload"
        @print="print"
        @excel="excel"
      >
        <template #before>
          <warehouse-pick
            v-if="screen.filters.warehouse && warehouses.length"
            name="warehouse"
            multiple
            icon="mdi-warehouse"
            :items="warehouseItems"
            :value="selectedWarehouseIds"
            :title="$t('stock')"
            :search-label="$t('warehouse_shell.search_warehouses')"
            :placeholder="$t('warehouse_shell.pick_warehouse')"
            :all-label="$t('warehouse_shell.all_warehouses_n', { n: warehouses.length })"
            @input="setWarehouseIds"
          ></warehouse-pick>
        </template>
        <template #filters>
          <warehouse-pick
            v-if="screen.filters.view"
            name="view"
            plain
            :items="viewItems"
            :value="filters.showType"
            :title="$t('view')"
            :disabled="!filters.warehouse.length"
            @input="setFilterValue('showType', $event)"
          ></warehouse-pick>
          <warehouse-pick
            v-if="screen.filters.docType"
            name="docType"
            labeled
            :items="docTypeItems"
            :value="filters.documentType"
            :title="$t('warehouse.document_type')"
            :placeholder="$t('warehouse.document_type')"
            @input="setFilterValue('documentType', $event)"
          ></warehouse-pick>
          <warehouse-pick
            v-if="screen.filters.status"
            name="status"
            labeled
            :items="statusItems"
            :value="filters.status"
            :title="$t('status')"
            @input="setFilterValue('status', $event)"
          ></warehouse-pick>
          <warehouse-pick
            v-if="screen.filters.suppliers"
            name="suppliers"
            multiple
            icon="mdi-truck-outline"
            :items="supplierItems"
            :value="selectedSupplierIds"
            :title="$t('supplier')"
            :search-label="$t('warehouse_shell.search_suppliers')"
            :placeholder="$t('supplier')"
            :all-label="$t('warehouse_shell.all_suppliers_n', { n: suppliers.length })"
            @input="setSupplierIds"
          ></warehouse-pick>
          <warehouse-pick
            v-if="screen.filters.grocery"
            name="grocery"
            labeled
            icon="mdi-food-apple-outline"
            :items="groceryItems"
            :value="filters.groceryCode ? filters.groceryCode.code : null"
            :title="$t('code')"
            :placeholder="$t('warehouse_shell.grocery')"
            :search-label="$t('search')"
            @input="setGroceryCode"
          ></warehouse-pick>
        </template>
      </report-filter-bar>

      <div class="rd-page">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ screen.title }}</h1>
            <p class="rd-head__sub">{{ headerSubtitle }}</p>
          </div>
          <div class="rd-head__right">
            <report-kpis :kpis="kpis"></report-kpis>
          </div>
        </div>

        <report-state v-if="!ready" kind="loading"></report-state>
        <report-state
          v-else-if="!warehouses.length"
          icon="mdi-warehouse"
          :title="$t('no_warehouses')"
          :text="$t('no_warehouses_added_yet')"
        >
          <button type="button" class="ui-btn ui-btn--primary" @click="warehouseDialog = true">
            <v-icon size="16" color="#ffffff">mdi-plus</v-icon>
            {{ $t("new_warehouse") }}
          </button>
        </report-state>
        <report-state
          v-else-if="needsWarehouse"
          icon="mdi-warehouse"
          :title="$t('warehouse_shell.pick_warehouse')"
          :text="$t('warehouse_shell.pick_warehouse_text')"
        ></report-state>
        <report-desktop-legacy
          v-else-if="screen.legacy"
          :key="currentKey"
          :component="screen.component"
          @toast="showToast"
        ></report-desktop-legacy>
        <component
          :is="screen.component"
          v-else
          :key="currentKey"
          @kpis="kpis = $event"
          @loading="loading = $event"
          @open-panel="openPanel"
          @toast="showToast"
        ></component>
      </div>
    </div>

    <report-detail-panel
      :stack="panelStack"
      @close="closePanel"
      @back="panelBack"
      @open="openPanel"
      @toast="showToast"
    ></report-detail-panel>
    <transition name="rd-fade">
      <div v-if="panelStack.length" class="rd-scrim" @click="closePanel"></div>
    </transition>

    <report-help-popover :open="helpOpen" :shortcuts="shortcuts" @close="helpOpen = false"></report-help-popover>
    <ui-toast :toast="toast" @action="runToastAction"></ui-toast>

    <warehouse-dialog v-model="warehouseDialog" :edited-item="editedWarehouse" @create="onWarehouseCreated" />
  </div>
</template>

<script>
import "@/components/report/desktop/reportDesktop.scss";
import "./warehouseShell.scss";
import ReportNav from "@/components/report/shared/ReportNav.vue";
import ReportHelpPopover from "@/components/report/shared/ReportHelpPopover.vue";
import ReportKpis from "@/components/report/shared/ReportKpis.vue";
import ReportFilterBar from "@/components/report/desktop/ReportFilterBar.vue";
import ReportDetailPanel from "@/components/report/desktop/ReportDetailPanel.vue";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportDesktopLegacy from "@/components/report/desktop/screens/ReportDesktopLegacy.vue";
import hasReportShell from "@/components/report/shared/hasReportShell";
import WarehouseDialog from "@/components/warehouse/dialogs/WarehouseDialog.vue";
import WarehousePick from "./WarehousePick.vue";
import hasWarehouseShellState from "./hasWarehouseShellState";

const DEFAULT_QUICK = ["today", "yesterday", "this_week"];

/**
 * Magacin na računaru: gornja navigacija po grupama, traka filtera (magacini, period, smene, dobavljači, status,
 * pretraga), zaglavlje sa brojkama, ekran i panel sa desne strane. Trebovanje je novi ekran; ostali tabovi su stari
 * tabovi u novoj ljusci (isto učitavanje, štampa, Excel i dijalozi). Izbori su u store-u `warehouse/filters`
 * (kao do sada), tab i period u adresi. Raspored, panel, prečice i obaveštenja su u hasReportShell.
 */
export default {
  name: "WarehouseDesktop",
  mixins: [hasReportShell, hasWarehouseShellState],
  components: {
    ReportNav,
    ReportHelpPopover,
    ReportKpis,
    ReportFilterBar,
    ReportDetailPanel,
    ReportState,
    ReportDesktopLegacy,
    WarehouseDialog,
    WarehousePick,
  },
  data: () => ({
    defaultQuick: DEFAULT_QUICK,
  }),
  watch: {
    currentKey(key, previous) {
      if (key === previous) {
        return;
      }
      this.kpis = [];
      this.loading = false;
      this.closePanel();
      window.scrollTo(0, 0);
    },
  },
};
</script>
